import type { Stage, UseChecklistResult } from '../types';
import styles from '../App.module.css';
import { Mascot, type MascotId } from './mascots';
import { MindCards } from './MindCards';
import { Tips } from './Tips';
import { Checklist, type ChecklistEditor } from './Checklist';
import { AdminGuide } from './AdminGuide';
import { Shopping, type ShoppingEditor } from './Shopping';
import { CareScenes } from './CareScenes';
import { WarningSigns } from './WarningSigns';
import { PartnerList } from './PartnerList';
import { Exercises } from './Exercises';
import { useLanguage } from '../i18n/context';
import { getUi } from '../i18n/ui';
import { useReveal } from '../hooks/useReveal';

interface StageViewProps {
  stage: Stage;
  checklist: UseChecklistResult;
  checklistEditor?: ChecklistEditor;
  shoppingEditor?: Omit<ShoppingEditor, 'stageId'>;
}

function SectionHeader({ mascot, children }: { mascot: MascotId; children: string }) {
  return (
    <div className={styles.sectionHeader}>
      <Mascot id={mascot} size={40} />
      <h3 className={styles.sectionTitle}>{children}</h3>
    </div>
  );
}

export function StageView({ stage, checklist, checklistEditor, shoppingEditor }: StageViewProps) {
  const { locale } = useLanguage();
  const ui = getUi(locale);
  useReveal(stage.id);

  return (
    <article className={styles.stage}>
      <header className={styles.stageHeader} data-reveal>
        <div className={styles.stageMeta}>
          <span className={styles.stageWeeks}>{stage.weeks}</span>
        </div>
        <h2 className={styles.stageTitle}>{stage.title}</h2>
        <p className={styles.stageSubtitle}>{stage.subtitle}</p>
        <p className={styles.stageDescription}>{stage.description}</p>
      </header>

      <section className={styles.section} data-reveal>
        <SectionHeader mascot="heart">{ui.stageView.mindCardsTitle}</SectionHeader>
        <MindCards cards={stage.mindCards} />
      </section>

      {stage.tips && stage.tips.length > 0 && (
        <section className={styles.section} data-reveal>
          <SectionHeader mascot="star">{ui.stageView.tipsTitle}</SectionHeader>
          <Tips items={stage.tips} />
        </section>
      )}

      {stage.exercises && stage.exercises.length > 0 && (
        <section className={styles.section} data-reveal>
          <SectionHeader mascot="heart">{ui.stageView.exercisesTitle}</SectionHeader>
          <Exercises items={stage.exercises} />
        </section>
      )}

      <section className={styles.section} data-reveal>
        <SectionHeader mascot="momo">{ui.stageView.checklistTitle}</SectionHeader>
        <Checklist stage={stage} checklist={checklist} editor={checklistEditor} />
      </section>

      {stage.adminGuide && stage.adminGuide.length > 0 && (
        <section className={styles.section} data-reveal>
          <SectionHeader mascot="nurse">{ui.stageView.adminGuideTitle}</SectionHeader>
          <AdminGuide items={stage.adminGuide} />
        </section>
      )}

      <section className={styles.section} data-reveal>
        <SectionHeader mascot="shop">{ui.stageView.shoppingTitle}</SectionHeader>
        <Shopping
          items={stage.shopping}
          editor={
            shoppingEditor
              ? {
                  stageId: stage.id,
                  ...shoppingEditor,
                }
              : undefined
          }
        />
      </section>

      {stage.careScenes && stage.careScenes.length > 0 && (
        <section className={styles.section} data-reveal>
          <SectionHeader mascot="sleep">{ui.stageView.careScenesTitle}</SectionHeader>
          <CareScenes items={stage.careScenes} />
        </section>
      )}

      <section className={styles.section} data-reveal>
        <SectionHeader mascot="nurse">{ui.stageView.warningsTitle}</SectionHeader>
        <WarningSigns items={stage.warnings} />
      </section>

      <section className={styles.section} data-reveal>
        <SectionHeader mascot="papa">{ui.stageView.partnerTitle}</SectionHeader>
        <PartnerList items={stage.partner} />
      </section>
    </article>
  );
}
